import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styled components
const LoginContainer = styled.div`
  display: flex;
  min-height: 100vh;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled(motion.div)`
  flex: 1;
  background: linear-gradient(135deg, #4f46e5, #8b5cf6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
`;

const RightPanel = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: #f8fafc;
`;

const LoginForm = styled(motion.div)`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled(motion.h1)`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #1e293b;
  text-align: center;
`;

const InputGroup = styled(motion.div)`
  margin-bottom: 1.5rem;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
`;

const InputField = styled(motion.input)`
  width: 100%;
  padding: 0.875rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  &:focus {
    border-color: #818cf8;
    box-shadow: 0 0 0 3px rgba(129, 140, 248, 0.2);
    outline: none;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #4f46e5, #8b5cf6);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
  cursor: pointer;
`;

const ForgotLink = styled(motion.a)`
  display: block;
  text-align: center;
  margin-top: 1.5rem;
  color: #64748b;
  font-size: 0.875rem;
  text-decoration: none;
  &:hover {
    color: #4f46e5;
  }
`;

const HeroImage = styled(motion.img)`
  max-width: 80%;
  height: auto;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  z-index: 2;
`;

const DecorationCircle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const CenterLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate login API call
    setTimeout(() => {
      setIsSubmitting(false);
    }, 2000);
  };

  return (
    <LoginContainer>
      <LeftPanel
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <DecorationCircle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          style={{
            width: '300px',
            height: '300px',
            top: '-100px',
            right: '-100px'
          }}
        />
        <DecorationCircle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          style={{
            width: '200px',
            height: '200px',
            bottom: '-50px',
            left: '-50px'
          }}
        />
        
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          style={{ textAlign: 'center', zIndex: 3 }}
        >
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Welcome Back!</h1>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.9 }}>
            Access your center management dashboard and tools
          </p>
        </motion.div>
        
        <HeroImage
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
          alt="Center Management"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          whileHover={{ scale: 1.02 }}
        />
      </LeftPanel>

      <RightPanel>
        <LoginForm
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <FormTitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Center Login
          </FormTitle>

          <motion.form
            onSubmit={handleSubmit}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <InputGroup variants={itemVariants}>
              <InputLabel>Email Address</InputLabel>
              <InputField
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@center.com"
                required
                whileFocus={{ scale: 1.02 }}
              />
            </InputGroup>

            <InputGroup variants={itemVariants}>
              <InputLabel>Password</InputLabel>
              <InputField
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                whileFocus={{ scale: 1.02 }}
              />
            </InputGroup>

            <LoginButton
              type="submit"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              animate={isSubmitting ? { scale: 0.98 } : { scale: 1 }}
            >
              {isSubmitting ? 'Logging in...' : 'Login to Dashboard'}
            </LoginButton>

            <ForgotLink
              href="#"
              variants={itemVariants}
              whileHover={{ x: 2 }}
            >
              Forgot password?
            </ForgotLink>
          </motion.form>
        </LoginForm>
      </RightPanel>
    </LoginContainer>
  );
};

export default CenterLogin;